/**
 * KeyLocations - Beautiful operational hubs section
 * Highlights Thoothukudi, Vizhinjam, and Chennai ports and services.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaAnchor, FaShip } from 'react-icons/fa';
import { StaggerContainer, StaggerItem } from '../ui/AnimatedSection';
import SectionHeader from '../ui/SectionHeader';

const locations = [
  {
    city: 'Thoothukudi',
    title: 'Corporate HQ & Main Port Base',
    port: 'V.O. Chidambaranar Port (Tuticorin)',
    desc: 'Our primary headquarters and core operations center. We manage full vessel husbanding, custom clearance, logistics integrations, and green port electrification services here.',
    features: [
      'Corporate Headquarters office',
      '24/7 Dedicated Port Operations Desk',
      'Direct Port Trust & Custom House Liaison',
      'Complete Vessel Agency & Husbanding'
    ],
    accent: 'var(--orange)',
    accentDim: 'var(--orange-dim)',
    accentBorder: 'var(--orange-border)',
    icon: FaAnchor,
    tag: 'Headquarters'
  },
  {
    city: 'Vizhinjam',
    title: 'Strategic Transshipment Hub',
    port: 'Adani Vizhinjam International Transshipment Port',
    desc: 'Specialized shipping agency coordination and owner representation at India\'s premier deepwater container transshipment terminal. Delivering swift port stays and turnaround solutions.',
    features: [
      'Vessel Husbanding & Port Agency',
      'Transshipment Cargo Coordination',
      'Owner Representation & Protection',
      'Crew Welfare & Shore Logistics'
    ],
    accent: 'var(--purple-accent)',
    accentDim: 'var(--purple-accent-dim)',
    accentBorder: 'var(--purple-accent-border)',
    icon: FaShip,
    tag: 'Deepwater Port Hub'
  },
  {
    city: 'Chennai',
    title: 'Regional Logistics Gateway',
    port: 'Chennai Port & Kamarajar Port (Ennore)',
    desc: 'Our major gateway for multimodal freight forwarding (sea and air), custom house clearance, project cargo logistics (ODC heavy lifts), and strategic supply chain warehousing.',
    features: [
      'Multimodal Freight Forwarding (Sea/Air)',
      'Customs Brokerage (CHA Clearance)',
      'ODC & Project Cargo Operations',
      '3PL Supply Chain & Warehousing'
    ],
    accent: 'var(--teal)',
    accentDim: 'var(--teal-dim)',
    accentBorder: 'rgba(18, 122, 130, 0.3)',
    icon: FaMapMarkerAlt,
    tag: 'Logistics Gateway'
  }
];

const KeyLocations = () => {
  return (
    <section style={{
      padding: '4.5rem 0',
      background: 'var(--bg-page)',
      overflow: 'hidden',
      borderBottom: '1px solid rgba(10,17,40,0.05)'
    }}>
      <div className="container-xxl px-4 px-lg-5">
        
        <SectionHeader
          tag="Our Port Network"
          title="Key Operating Locations"
          subtitle="Providing deep infrastructure, localized compliance expertise, and maritime logistics command across key subcontinent trade gateways."
        />

        <StaggerContainer className="row g-4" staggerDelay={0.08}>
          {locations.map((loc, i) => {
            const IconComp = loc.icon;
            return (
              <StaggerItem key={i} className="col-lg-4">
                <motion.div
                  whileHover={{ y: -6, borderColor: loc.accent }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    padding: '2.5rem 2rem',
                    height: '100%',
                    border: `1px solid rgba(10, 17, 40, 0.05)`,
                    borderTop: `4px solid ${loc.accent}`,
                    boxShadow: 'var(--shadow-sm)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'border-color 0.25s ease'
                  }}
                  className="location-card"
                >
                  <div>
                    {/* Header: Tag and Icon */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <span style={{
                        background: loc.accentDim,
                        border: `1px solid ${loc.accentBorder}`,
                        borderRadius: '30px',
                        padding: '0.2rem 0.75rem',
                        fontSize: '0.62rem',
                        fontWeight: '700',
                        color: loc.accent === 'var(--orange)' ? 'var(--orange)' : loc.accent === 'var(--purple-accent)' ? 'var(--purple-accent-light)' : 'var(--teal-light)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {loc.tag}
                      </span>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: 2,
                        background: loc.accentDim, border: `1px solid ${loc.accentBorder}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <IconComp size={14} color={loc.accent === 'var(--orange)' ? 'var(--orange)' : loc.accent === 'var(--purple-accent)' ? 'var(--purple-accent-light)' : 'var(--teal-light)'} />
                      </div>
                    </div>

                    {/* Location Name & Port */}
                    <h3 style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 800,
                      color: 'var(--navy)',
                      fontSize: '1.4rem',
                      marginBottom: '0.2rem',
                      letterSpacing: '-0.02em'
                    }}>
                      {loc.city}
                    </h3>
                    <h5 style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 700,
                      color: 'var(--text-muted)',
                      fontSize: '0.78rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      marginBottom: '1rem',
                      lineHeight: 1.3
                    }}>
                      {loc.port}
                    </h5>

                    {/* Description */}
                    <p style={{
                      color: 'var(--text-body)',
                      fontSize: '0.85rem',
                      lineHeight: '1.7',
                      marginBottom: '1.8rem'
                    }}>
                      {loc.desc}
                    </p>
                  </div>

                  {/* Highlights checklist */}
                  <ul style={{
                    padding: 0,
                    margin: 0,
                    borderTop: '1px solid rgba(10, 17, 40, 0.05)',
                    paddingTop: '1.2rem'
                  }}>
                    {loc.features.map((feat, j) => (
                      <li key={j} style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '0.5rem',
                        fontSize: '0.78rem',
                        color: 'var(--text-body)',
                        marginBottom: '0.4rem',
                        lineHeight: 1.4
                      }}>
                        <span style={{
                          color: loc.accent === 'var(--orange)' ? 'var(--orange)' : loc.accent === 'var(--purple-accent)' ? 'var(--purple-accent-light)' : 'var(--teal-light)',
                          fontSize: '0.55rem',
                          flexShrink: 0
                        }}>—</span>
                        {feat}
                      </li>
                    ))}
                  </ul>

                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>

      </div>

      <style>{`
        .location-card:hover {
          box-shadow: var(--shadow-md) !important;
        }
      `}</style>
    </section>
  );
};

export default KeyLocations;
